// COP — the SYNTHETIC sandbox picture (?mock=1 only).
//
// CONTRACT:P1-CANONICAL-ENVELOPE.1.0
//
// The demonstrated picture is the WIRED multi-INT Strait-of-Hormuz dataset
// (../scenarios/hormuz.ts → hormuz.json, 121 contacts with REAL corpus markings),
// published as CanonicalEnvelopes onto the bus → LOCAL PDP (I4) → render path so the
// clearance filter bites on the WHOLE picture (permit → show / deny → absent). This
// module is a thin shim that delegates to the SELECTED theater.
//
// THEATER SELECTION (0.2b): three named multi-INT theaters are registered below —
// hormuz (default), taiwan, levant. The active one is chosen by the `?scenario=`
// URL param (e.g. ?mock=1&scenario=taiwan); an unknown/absent value falls back to
// hormuz. Each theater publishes onto the SAME bus → PDP → render path. A richer
// in-app scenario-switch UI (live re-load without a reload) is followup work — see
// TODO.md#scenario-switch-ui; this is the minimal SELECTABLE registration.
//
// NOTE: the DEFAULT feed is now the LIVE TAK feed (apps/cop/src/host/feed.ts is
// live-only — there is NO synthetic fallback). `startScenario` is reached ONLY via
// the explicit ?mock=1 design sandbox (labeled "EXERCISE · synthetic"). The legacy
// Mid-Atlantic synthetic generator that used to live here was removed in the
// 2026-06-21 scrub (it had zero callers once the feed went live-only).

import type { EnvelopeBus } from "./envelopeBus.ts";
import { startHormuzScenario } from "../scenarios/hormuz.ts";
import { startTaiwanScenario } from "../scenarios/taiwan.ts";
import { startLevantScenario } from "../scenarios/levant.ts";
import { startGlobalIntelScenario } from "../scenarios/global-intel.ts";
import { startStressScenario } from "../scenarios/stress.ts";

/** Dev-only build guard (Vite sets import.meta.env.DEV; guarded for a non-Vite/headless import). */
const IS_DEV = ((): boolean => {
  try {
    return Boolean((import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV);
  } catch {
    return false;
  }
})();

export interface ScenarioHandle {
  stop(): void;
}

/** A registered theater: its stable id + its bus-starting loader (each mirrors
 *  hormuz.ts's start signature). */
interface ScenarioEntry {
  label: string;
  start: (bus: EnvelopeBus, tickMs?: number) => ScenarioHandle;
}

/** THE SCENARIO REGISTRY — the SELECTABLE named theaters (?scenario=<id>). hormuz is
 *  the default. Adding a theater is one entry here + its loader module. */
export const SCENARIO_REGISTRY: Record<string, ScenarioEntry> = {
  hormuz: { label: "Strait of Hormuz", start: startHormuzScenario },
  taiwan: { label: "Taiwan Strait", start: startTaiwanScenario },
  levant: { label: "Levant", start: startLevantScenario },
  "global-intel": { label: "Global Intel", start: startGlobalIntelScenario },
  // DEV-ONLY synthetic perf theater (?mock=1&scenario=stress[&stressN=N]) — never shipped.
  ...(IS_DEV ? { stress: { label: "Stress (perf)", start: startStressScenario } } : {}),
};

/** The default theater id when none / an unknown one is selected. */
export const DEFAULT_SCENARIO_ID = "hormuz";

/** Resolve the active theater id from the `?scenario=` URL param. Unknown/absent ⇒
 *  the default (hormuz). Defensive against a non-browser context (returns default). */
export function activeScenarioId(): string {
  try {
    const id = new URLSearchParams(window.location.search).get("scenario");
    if (id && id in SCENARIO_REGISTRY) return id;
  } catch {
    // no window / no search — fall through to the default.
  }
  return DEFAULT_SCENARIO_ID;
}

/** The synthetic-sandbox picture (?mock=1): the SELECTED multi-INT theater (hormuz
 *  by default; taiwan/levant via ?scenario=<id>), published client-side as real
 *  PDP-filtered envelopes. Delegates to the theater's loader module. */
export function startScenario(bus: EnvelopeBus, tickMs = 1_000): ScenarioHandle {
  const entry = SCENARIO_REGISTRY[activeScenarioId()] ?? SCENARIO_REGISTRY[DEFAULT_SCENARIO_ID];
  return entry.start(bus, tickMs);
}

/** Start an EXPLICIT theater by id (the live scenario-switch path — TODO#scenario-switch-ui).
 *  Unknown id ⇒ the default theater (never throws, never empty). Same bus → PDP → render
 *  path as startScenario; the caller (the host) is responsible for CLEARING the prior
 *  theater's picture before calling this so the new theater can't layer over stale tracks. */
export function startScenarioById(bus: EnvelopeBus, tickMs: number, id: string): ScenarioHandle {
  const entry = SCENARIO_REGISTRY[id] ?? SCENARIO_REGISTRY[DEFAULT_SCENARIO_ID];
  return entry.start(bus, tickMs);
}

/** The selectable theaters as `{ id, label }` rows (registry order) — the small
 *  accessor the layers UI renders to list/highlight the active theater. Pure read. */
export function scenarioList(): Array<{ id: string; label: string }> {
  return Object.entries(SCENARIO_REGISTRY).map(([id, entry]) => ({ id, label: entry.label }));
}
